import { CommunicationWithRelations } from "@/types/communication.types";
import { fetchCommunications } from "./communicationService";

/**
 * Converts communications data to CSV format
 */
export const communicationsToCSV = (communications: CommunicationWithRelations[]): string => {
  // Define headers
  const headers = [
    "ID",
    "Subject",
    "Type",
    "Date",
    "Content",
    "Organization",
    "Contact",
    "Opportunity",
    "Status",
    "Sentiment",
    "Tags",
  ].join(",");
  
  // Map communications to CSV rows
  const rows = communications.map((comm) => {
    // Format tags as a semicolon-separated list
    const tags = Array.isArray(comm.tags) 
      ? comm.tags.join("; ")
      : "";
    
    // Process content to escape commas and quotes
    const content = comm.content
      ? `"${comm.content.replace(/"/g, '""')}"`
      : "";
    
    return [
      comm.id,
      `"${comm.subject.replace(/"/g, '""')}"`,
      comm.type,
      new Date(comm.date).toISOString(),
      content,
      comm.organization?.name || "",
      comm.contact ? `${comm.contact.first_name} ${comm.contact.last_name}` : "",
      comm.opportunity?.name || "",
      comm.status || "completed",
      comm.sentiment || "neutral",
      `"${tags}"`,
    ].join(",");
  });
  
  // Combine headers and rows
  return [headers, ...rows].join("\n");
};

/**
 * Export communications as a CSV file
 */
export const exportCommunicationsToCSV = async (
  filters?: {
    organization_id?: string;
    contact_id?: string;
    opportunity_id?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    types?: string[];
  }
): Promise<void> => {
  try {
    // Fetch communications with filters
    const communications = await fetchCommunications(filters);
    
    // Convert to CSV
    const csv = communicationsToCSV(communications);
    
    // Create a blob with CSV content
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    
    // Create a download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `communications_export_${new Date().toISOString().split("T")[0]}.csv`);
    link.style.visibility = "hidden";
    
    // Append to body, click and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    throw error;
  }
};

/**
 * Format communications data for Excel export
 */
export const communicationsToExcelData = (communications: CommunicationWithRelations[]) => {
  return communications.map((comm) => ({
    ID: comm.id,
    Subject: comm.subject,
    Type: comm.type,
    Date: new Date(comm.date).toISOString(),
    Content: comm.content || "",
    Organization: comm.organization?.name || "",
    Contact: comm.contact 
      ? `${comm.contact.first_name} ${comm.contact.last_name}` 
      : "",
    Opportunity: comm.opportunity?.name || "",
    Status: comm.status || "completed",
    Sentiment: comm.sentiment || "neutral",
    Tags: Array.isArray(comm.tags) ? comm.tags.join(", ") : "",
  }));
};

/**
 * Get stats for communications export
 */
export const getCommunicationExportStats = (communications: CommunicationWithRelations[]) => {
  const totalCount = communications.length;
  
  // Count by type
  const typeCount: Record<string, number> = {};
  communications.forEach((comm) => {
    const type = comm.type || "unknown";
    typeCount[type] = (typeCount[type] || 0) + 1;
  });
  
  // Count by organization
  const orgCount: Record<string, number> = {};
  communications.forEach((comm) => {
    if (comm.organization) {
      const orgName = comm.organization.name;
      orgCount[orgName] = (orgCount[orgName] || 0) + 1;
    }
  });
  
  // Count by sentiment
  const sentimentCount: Record<string, number> = {};
  communications.forEach((comm) => {
    const sentiment = comm.sentiment || "neutral";
    sentimentCount[sentiment] = (sentimentCount[sentiment] || 0) + 1;
  });
  
  return {
    totalCount,
    typeCount,
    orgCount,
    sentimentCount,
  };
};